// Worm SPAWN-IN animation designs (dev gallery: /spawn). During the round countdown each
// worm "materializes" onto the board (staggered, so they pop in one after another). The
// host draws the worm itself through the `drawWorm` callback (scale/alpha/offset), and the
// design decides how to call it + what FX to add around the spawn point - the same pattern
// the power-up animations use for their token.
//
// drawSpawnAnim(ctx, x, y, headR, color, p, t, variant, drawWorm)
//   x, y    : spawn point (head position) in pixels
//   headR   : head radius in pixels
//   color   : the worm's colour
//   p       : spawn progress 0..1 (0 = nothing, 1 = fully in place)
//   t       : absolute seconds (shimmer/rotation)
//   drawWorm: host callback drawing the worm; opts {scale, alpha, dx, dy} all default to 1/1/0/0.

export interface SpawnWormDraw {
  (opts?: { scale?: number; alpha?: number; dx?: number; dy?: number }): void;
}

export const SPAWN_ANIMS: { n: number; label: string }[] = [
  { n: 1, label: "Materialize (scale + soft ring)" },
  { n: 2, label: "Plain fade" },
  { n: 3, label: "Beam down" },
  { n: 4, label: "Portal drop" },
  { n: 5, label: "Dust pop" },
  { n: 6, label: "Pixel assemble" },
  { n: 7, label: "Ring implosion" },
  { n: 8, label: "Meteor impact" },
  { n: 9, label: "Bubble pop" },
  { n: 10, label: "Spark spiral" },
];

const TAU = Math.PI * 2;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const easeOut = (v: number) => 1 - Math.pow(1 - v, 3);
const easeOutBack = (v: number) => { const c = 1.70158; const u = v - 1; return 1 + (c + 1) * u * u * u + c * u * u; };
const rnd = (i: number) => { const s = Math.sin(i * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
function rgba(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${clamp01(a)})`;
}
function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) {
  ctx.fillStyle = fill; ctx.beginPath(); ctx.arc(x, y, Math.max(0.4, r), 0, TAU); ctx.fill();
}
function ring(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, w: number, stroke: string) {
  ctx.strokeStyle = stroke; ctx.lineWidth = Math.max(0.6, w); ctx.beginPath(); ctx.arc(x, y, Math.max(0.5, r), 0, TAU); ctx.stroke();
}

export function drawSpawnAnim(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  headR: number,
  color: string,
  p: number,
  t: number,
  variant: number,
  drawWorm: SpawnWormDraw,
): void {
  const R = Math.max(4, headR);
  const e = easeOut(clamp01(p));
  ctx.save();
  ctx.lineCap = "round";
  switch (variant) {
    case 2: { // plain fade (the baseline to beat)
      drawWorm({ alpha: e });
      break;
    }
    case 3: { // beam down: a light column narrows and lifts away as the worm forms
      drawWorm({ scale: e, alpha: e });
      if (p < 1) {
        const bw = R * (2.2 - e * 1.8);
        const grad = 1 - e;
        ctx.fillStyle = rgba("#ffffff", 0.35 * grad);
        ctx.fillRect(x - bw / 2, y - 120, bw, 120);
        ctx.fillStyle = rgba(color, 0.4 * grad);
        ctx.fillRect(x - bw * 0.3, y - 120, bw * 0.6, 120);
        dot(ctx, x, y, R * (0.6 + grad * 0.8), rgba("#ffffff", 0.5 * grad));
      }
      break;
    }
    case 4: { // portal drop: an ellipse gate above, the worm slides down out of it
      const gate = Math.sin(Math.min(1, p * 1.2) * Math.PI); // opens then closes
      const drop = R * 3 * (1 - e);
      drawWorm({ scale: 0.4 + 0.6 * e, alpha: e, dy: -drop });
      ctx.strokeStyle = rgba(color, 0.9 * gate);
      ctx.lineWidth = R * 0.22;
      ctx.beginPath(); ctx.ellipse(x, y - R * 3, R * 1.6 * gate + 0.001, R * 0.55 * gate + 0.001, 0, 0, TAU); ctx.stroke();
      break;
    }
    case 5: { // dust pop: pop in with overshoot + a puff of dust (matches the pickup dust)
      drawWorm({ scale: easeOutBack(clamp01(p)), alpha: Math.min(1, p * 3) });
      if (p < 0.8) {
        const d = p / 0.8;
        for (let k = 0; k < 14; k++) {
          const ang = (k / 14) * TAU + rnd(k) * 1.5;
          const dist = R * (0.5 + easeOut(d) * 1.6);
          dot(ctx, x + Math.cos(ang) * dist, y + Math.sin(ang) * dist, R * 0.14 * (1 - d), rgba("#cbd5e1", 0.6 * (1 - d)));
        }
      }
      break;
    }
    case 6: { // pixel assemble: glitch squares converge into the worm
      drawWorm({ alpha: clamp01(p * 1.6 - 0.4) });
      if (p < 0.85) {
        const d = p / 0.85;
        ctx.fillStyle = rgba(color, 0.85);
        for (let k = 0; k < 10; k++) {
          const ang = rnd(k) * TAU;
          const dist = R * 3.2 * (1 - easeOut(clamp01(d * 1.1 - rnd(k + 5) * 0.1)));
          const sz = R * (0.2 + rnd(k + 9) * 0.22) * (1 - d * 0.5);
          ctx.fillRect(x + Math.cos(ang) * dist - sz / 2, y + Math.sin(ang) * dist - sz / 2, sz, sz);
        }
      }
      break;
    }
    case 7: { // ring implosion: rings collapse inward, then a small pop
      drawWorm({ scale: e, alpha: e });
      for (let k = 0; k < 3; k++) {
        const u = clamp01(p * 1.3 - k * 0.15);
        if (u <= 0 || u >= 1) continue;
        ring(ctx, x, y, R * 3 * (1 - easeOut(u)), R * 0.14 * u, rgba(color, u));
      }
      if (p > 0.8) ring(ctx, x, y, R * (0.4 + (p - 0.8) * 5 * R * 0.08), R * 0.12 * (1 - p) * 5, rgba("#ffffff", (1 - p) * 3));
      break;
    }
    case 8: { // meteor impact: a streak slams in, flash, worm appears in the crater
      const hit = 0.45;
      if (p < hit) {
        const u = p / hit;
        const sx = x + R * 14 * (1 - u), sy = y - R * 10 * (1 - u);
        ctx.strokeStyle = rgba(color, 0.95);
        ctx.lineWidth = R * 0.3;
        ctx.beginPath(); ctx.moveTo(sx + R, sy - R * 0.7); ctx.lineTo(sx, sy); ctx.stroke();
        dot(ctx, sx, sy, R * 0.4, "#ffffff");
      } else {
        const u = (p - hit) / (1 - hit);
        drawWorm({ scale: easeOutBack(u), alpha: u });
        ring(ctx, x, y, R * (0.4 + easeOut(u) * 2.2), R * 0.16 * (1 - u), rgba(color, 1 - u));
        for (let k = 0; k < 8; k++) {
          const ang = (k / 8) * TAU + rnd(k);
          const dist = R * (0.4 + easeOut(u) * 1.8);
          dot(ctx, x + Math.cos(ang) * dist, y + Math.sin(ang) * dist, R * 0.12 * (1 - u), rgba(color, 1 - u));
        }
      }
      break;
    }
    case 9: { // bubble pop: grows inside a soap bubble that pops at the end
      const popAt = 0.8;
      drawWorm({ scale: Math.min(1, p / popAt), alpha: Math.min(1, p / popAt) });
      if (p < popAt) {
        const rr = R * (0.8 + (p / popAt) * 1.2);
        ring(ctx, x, y, rr, R * 0.1, rgba("#bfe3ff", 0.8));
        dot(ctx, x - rr * 0.35, y - rr * 0.35, rr * 0.18, rgba("#ffffff", 0.5));
      } else {
        const u = (p - popAt) / (1 - popAt);
        for (let k = 0; k < 8; k++) {
          const ang = (k / 8) * TAU;
          const dist = R * 2 * (1 + u);
          dot(ctx, x + Math.cos(ang) * dist, y + Math.sin(ang) * dist, R * 0.1 * (1 - u), rgba("#bfe3ff", 0.7 * (1 - u)));
        }
      }
      break;
    }
    case 10: { // spark spiral: sparks spiral inward and light the worm up
      drawWorm({ scale: e, alpha: e });
      if (p < 0.9) {
        const d = p / 0.9;
        for (let k = 0; k < 10; k++) {
          const u = clamp01(d * 1.15 - rnd(k) * 0.15);
          const ang = rnd(k + 3) * TAU + u * TAU * 1.5;
          const dist = R * 3 * (1 - easeOut(u));
          dot(ctx, x + Math.cos(ang) * dist, y + Math.sin(ang) * dist, R * (0.1 + rnd(k) * 0.1), rgba(color, 0.9 * (1 - u * 0.3)));
        }
      }
      break;
    }
    default: { // 1: materialize - scale-in + ONE soft expanding ring. The recommended default:
      // cheap, readable, clearly intentional (a bare fade reads as a glitch).
      drawWorm({ scale: e, alpha: e });
      if (p < 1) ring(ctx, x, y, R * (0.6 + e * 1.8), R * 0.16 * (1 - p), rgba(color, 0.8 * (1 - p)));
      break;
    }
  }
  ctx.restore();
}
